export const VENDOR_AGREEMENT = {
  slug: "vendor-agreement",
  title: "Vendor Agreement",
  role_label: "Vendor Agreement",
  company: "NContro",
  short_company: "NContro",
  effective_date: "August 21, 2026",
  version: "2.0",
  intro: [
    'This Vendor Agreement ("Agreement") is entered into between NContro ("Company," "Platform," "we," "our," or "us") and the individual or business registering as a Vendor ("Vendor," "you," or "your").',
    "By creating a Vendor account, listing services, accepting bookings, or receiving payments through the Platform, you agree to be legally bound by this Agreement.",
  ],
  articles: [
    {
      n: 1,
      title: "Purpose",
      body: [
        { p: "The Platform is an online marketplace that connects independent Vendors with Customers seeking event-related products and services." },
        { p: "The Company provides technology, payment facilitation, marketing, and communication tools. The Company does not employ Vendors, direct their work, or control how services are performed." },
      ],
    },
    {
      n: 2,
      title: "Independent Business",
      body: [
        { p: "You acknowledge and agree that you are an independent business and not an employee, partner, franchisee, agent, joint venturer, or representative of the Company." },
        { p: "Nothing in this Agreement creates an employment relationship." },
        { p: "You have no authority to bind the Company to any contract or obligation." },
      ],
    },
    {
      n: 3,
      title: "Eligibility",
      body: [
        { p: "To become a Vendor, you represent and warrant that:" },
        {
          list: [
            "You are at least 18 years of age.",
            "You have authority to enter this Agreement.",
            "Your business is legally formed, if applicable.",
            "You possess all licenses and permits required by law.",
            "You will maintain any insurance required by law or by the Platform.",
            "You will comply with all federal, state, and local laws.",
          ],
        },
        { p: "The Company may request proof of licensing, insurance, certifications, or permits at any time." },
      ],
    },
    {
      n: 4,
      title: "Vendor Profile",
      body: [
        { p: "You agree that all information provided on your Vendor profile shall remain accurate and current." },
        { p: "You are solely responsible for:" },
        {
          list: [
            "business name",
            "contact information",
            "pricing",
            "menus",
            "photographs",
            "service descriptions",
            "availability",
            "service areas",
            "business hours",
          ],
        },
        { p: "False or misleading information may result in suspension or termination." },
      ],
    },
    {
      n: 5,
      title: "Proposals",
      body: [
        { p: "Vendors may submit proposals to Customers." },
        { p: "Each proposal shall clearly disclose:" },
        {
          list: [
            "services included",
            "total estimated price",
            "required deposit",
            "payment schedule",
            "cancellation policy",
            "refund policy",
            "travel fees",
            "overtime fees",
            "taxes",
            "additional charges",
          ],
        },
        { p: "Once accepted by the Customer, the proposal becomes binding between the Vendor and Customer." },
      ],
    },
    {
      n: 6,
      title: "Deposits",
      body: [
        { p: "Each Vendor determines its own deposit requirements." },
        { p: "Deposit amounts must be clearly disclosed before payment." },
        { p: "The Vendor agrees not to demand additional deposits outside the Platform unless expressly permitted by the Platform." },
      ],
    },
    {
      n: 7,
      title: "Payments",
      body: [
        { p: "Customer payments shall be collected through the Platform." },
        { p: "The Platform shall deduct:" },
        {
          list: [
            "Platform commissions",
            "payment processing fees",
            "approved refunds",
            "chargebacks",
            "adjustments",
            "taxes withheld when required by law",
          ],
        },
        { p: "The remaining balance shall be remitted to the Vendor according to the Platform's payout schedule." },
        { p: "The Company reserves the right to delay payouts to investigate fraud, chargebacks, suspicious activity, or violations of this Agreement." },
      ],
    },
    {
      n: 8,
      title: "Platform Commission",
      body: [
        { p: "The Vendor agrees to pay the Company a Platform commission calculated as a percentage of each Customer payment processed through the Platform in connection with the Vendor's bookings (including deposit and remaining-balance payments)." },
        { p: "The current commission percentage is disclosed through the Vendor dashboard or the Company's fee schedule. The Company may modify the commission rate upon reasonable prior notice for future bookings; changes shall not retroactively apply to bookings whose Proposal was already accepted." },
        { p: "The commission is applied by the Platform's payment processor at the time of each Customer payment. Where a Customer payment is fully refunded through the Platform, any corresponding commission collected on that refunded amount is reversed by the payment processor to the extent supported by the processor and applicable law. Where a Customer payment is partially refunded, commission adjustments (if any) follow the payment processor's applicable rules and the Company's then-current refund handling." },
        { p: "Where a Customer cancels and the Vendor legitimately retains all or part of a Deposit or other payment under the accepted cancellation/refund policy for that Booking, the Company retains its commission on the amount so retained by the Vendor." },
        { p: "Chargebacks initiated by financial institutions may affect Vendor payouts and commissions; the Company may adjust or recoup commissions and payouts as reasonably necessary to reflect the final outcome of any chargeback or reversal, subject to applicable law." },
      ],
    },
    {
      n: 9,
      title: "Cancellations",
      body: [
        { p: "Each Vendor shall establish a written cancellation policy before accepting bookings." },
        { p: "The policy shall disclose:" },
        {
          list: [
            "refund deadlines",
            "deposit forfeiture",
            "rescheduling options",
            "cancellation fees",
          ],
        },
        { p: "Vendors are expected to honor their published cancellation policies." },
      ],
    },
    {
      n: 10,
      title: "Customer Service",
      body: [
        { p: "Vendors agree to:" },
        {
          list: [
            "perform services professionally;",
            "arrive on time;",
            "communicate promptly;",
            "maintain professional conduct;",
            "perform services as described;",
            "comply with all applicable laws.",
          ],
        },
      ],
    },
    {
      n: 11,
      title: "Licenses & Insurance",
      body: [
        { p: "The Vendor is solely responsible for maintaining all required:" },
        {
          list: [
            "business licenses",
            "food permits",
            "health department permits",
            "alcohol permits",
            "tax registrations",
            "insurance policies",
          ],
        },
        { p: "The Company may request documentation at any time." },
      ],
    },
    {
      n: 12,
      title: "Food Safety",
      body: [
        { p: "If providing food or beverages, the Vendor represents that all products shall be prepared, stored, transported, and served in compliance with applicable health laws and regulations." },
        { p: "The Vendor assumes all responsibility for food safety, allergens, contamination, foodborne illness, and related claims." },
      ],
    },
    {
      n: 13,
      title: "Taxes",
      body: [
        { p: "The Vendor is solely responsible for:" },
        {
          list: [
            "federal taxes;",
            "state taxes;",
            "local taxes;",
            "payroll taxes;",
            "sales taxes;",
            "business licenses;",
            "tax reporting.",
          ],
        },
        { p: "The Company does not provide tax advice." },
      ],
    },
    {
      n: 14,
      title: "Reviews",
      body: [
        { p: "Customers may review Vendors." },
        { p: "The Company may remove fraudulent or abusive reviews but is not obligated to remove negative reviews solely because the Vendor disagrees with them." },
      ],
    },
    {
      n: 15,
      title: "Prohibited Conduct",
      body: [
        { p: "Vendors shall not:" },
        {
          list: [
            "commit fraud;",
            "submit false information;",
            "discriminate unlawfully;",
            "harass Customers;",
            "use stolen payment methods;",
            "manipulate reviews;",
            "engage in deceptive advertising;",
            "violate intellectual property rights;",
            "use the Platform for unlawful purposes.",
          ],
        },
      ],
    },
    {
      n: 16,
      title: "Chargebacks",
      body: [
        { p: "The Vendor agrees to cooperate with the Company regarding chargebacks." },
        { p: "The Company may recover chargeback amounts from future payouts if the Vendor is determined to be responsible." },
      ],
    },
    {
      n: 17,
      title: "Intellectual Property",
      body: [
        { p: "The Vendor retains ownership of its trademarks, logos, photographs, menus, and marketing materials." },
        { p: "The Vendor grants the Company a non-exclusive, worldwide, royalty-free license to display such materials for purposes of operating, advertising, and promoting the Platform." },
      ],
    },
    {
      n: 18,
      title: "Indemnification",
      body: [
        { p: "The Vendor agrees to defend, indemnify, and hold harmless the Company, its officers, directors, employees, affiliates, contractors, and agents from any claims, damages, liabilities, losses, costs, judgments, or expenses, including reasonable attorneys' fees, arising out of:" },
        {
          list: [
            "Vendor services;",
            "Vendor negligence;",
            "personal injury;",
            "foodborne illness;",
            "property damage;",
            "intellectual property infringement;",
            "employment claims;",
            "tax obligations;",
            "Vendor violations of law; or",
            "breach of this Agreement.",
          ],
        },
      ],
    },
    {
      n: 19,
      title: "Limitation of Liability",
      body: [
        { p: "To the fullest extent permitted by law, the Company's liability shall not exceed the total commissions retained by the Company from the booking giving rise to the claim." },
        { p: "The Company shall not be liable for lost profits, business interruption, loss of goodwill, consequential damages, indirect damages, or punitive damages." },
      ],
    },
    {
      n: 20,
      title: "Termination",
      body: [
        { p: "The Company may suspend or terminate any Vendor account for:" },
        {
          list: [
            "fraud;",
            "repeated customer complaints;",
            "failure to perform services;",
            "violation of law;",
            "failure to maintain licenses;",
            "abusive conduct;",
            "non-payment of Platform fees;",
            "violation of this Agreement.",
          ],
        },
        { p: "Termination does not affect obligations accrued before termination." },
      ],
    },
    {
      n: 21,
      title: "Governing Law & Dispute Resolution",
      body: [
        { p: "This Agreement shall be governed by the laws of the Commonwealth of Virginia." },
        { p: "Any dispute arising from this Agreement shall first be submitted to good-faith informal negotiation. If not resolved, the dispute shall be submitted to binding arbitration in accordance with the Federal Arbitration Act and the applicable arbitration rules designated by the Company. Claims shall be brought only on an individual basis, and class or representative actions are waived to the fullest extent permitted by law." },
      ],
    },
    {
      n: 23,
      title: "Off-Platform Transactions & Circumvention",
      body: [
        { h: "23.1 Purpose" },
        { p: "The Platform invests substantial time and resources in marketing, technology, customer acquisition, payment processing, and customer support to connect Customers with Vendors. The restrictions contained in this Article are intended solely to protect those legitimate business interests." },

        { h: "23.2 Initial Bookings" },
        { p: "All initial bookings between a Customer and Vendor that originate through the Platform must be completed through the Platform." },
        { p: "A Vendor shall not request, encourage, induce, or assist a Customer in completing an initial booking outside the Platform for the purpose of avoiding Platform commissions, fees, or other charges." },

        { h: "23.3 Circumvention" },
        { p: "A Vendor agrees not to intentionally circumvent the Platform by:" },
        {
          list: [
            "directing Customers to book directly through another website or application;",
            "requesting or accepting payment outside the Platform — including but not limited to cash, personal or business check, wire transfer, money order, Venmo, Zelle, Cash App, PayPal, Apple Pay, Google Pay, cryptocurrency, or any other external payment method — for a booking initiated through the Platform;",
            "providing invoices, contracts, payment links, QR codes, or other payment methods outside the Platform before an initial booking is completed through the Platform;",
            "offering discounts or incentives intended to avoid Platform fees;",
            "encouraging Customers to cancel a Platform booking in order to transact outside the Platform; or",
            "otherwise taking actions intended to avoid the Company's commissions or fees.",
          ],
        },
        { p: "Nothing in this Article restricts ordinary business communication between Customers and Vendors that is reasonably necessary to plan and perform an event, including confirming logistics, discussing menu or service details, arranging site visits, and coordinating day-of execution. This Article is limited to intentional circumvention of Platform-generated bookings and fees." },

        { h: "23.4 Existing Customers" },
        { p: "Nothing in this Agreement prohibits a Vendor from conducting business with customers who had an established business relationship with the Vendor before the Customer first contacted the Vendor through the Platform." },
        { p: "The Company may request reasonable documentation demonstrating that such a prior business relationship existed." },

        { h: "23.5 Future Bookings" },
        { p: "Unless otherwise authorized in writing by the Company, a Vendor agrees that any additional bookings with a Customer first introduced through the Platform within twelve (12) months of the initial booking shall also be completed through the Platform." },
        { p: "Nothing in this section is intended to prohibit lawful competition or impose restrictions beyond what is reasonably necessary to protect the Company's legitimate business interests." },

        { h: "23.6 Monitoring" },
        { p: "The Company reserves the right to investigate suspected attempts to circumvent the Platform, including reviewing communications conducted through the Platform, booking history, payment records, customer reports, and other relevant information, subject to applicable law and the Company's Privacy Policy." },

        { h: "23.7 Remedies" },
        { p: "If the Company reasonably determines that a Vendor has intentionally violated this Article, the Company may, in its sole discretion:" },
        {
          list: [
            "suspend or terminate the Vendor's account;",
            "withhold unpaid commissions or payouts to the extent permitted by law;",
            "recover unpaid Platform commissions;",
            "cancel pending bookings;",
            "remove Vendor listings from the Platform; and",
            "pursue any other remedies available under this Agreement or applicable law.",
          ],
        },

        { h: "23.8 No Restriction on Independent Business" },
        { p: "Nothing contained in this Article shall prevent a Vendor from independently marketing its business, accepting business from customers obtained through other sources, or competing in the marketplace, provided the Vendor does not intentionally use the Platform to avoid commissions or fees owed to the Company." },
      ],
    },
    {
      n: 24,
      title: "Platform Integrity, Fraud Prevention, and Enforcement",
      body: [
        { h: "24.1 Commitment to Marketplace Integrity" },
        { p: "The Company is committed to maintaining a trustworthy, safe, and reliable marketplace for Customers and Vendors. To protect the integrity of the Platform, the Company reserves the right to investigate, monitor, restrict, suspend, or terminate any Vendor account that violates this Agreement or engages in conduct that may harm Customers, Vendors, or the Platform." },

        { h: "24.2 Fraudulent Activity" },
        { p: "The following activities are strictly prohibited and may result in immediate suspension or termination of a Vendor account:" },
        {
          list: [
            "Creating false or misleading business profiles;",
            "Using another person's identity or business information;",
            "Creating multiple Vendor accounts without the Company's authorization;",
            "Submitting false business licenses, insurance certificates, certifications, or permits;",
            "Misrepresenting business ownership or affiliations;",
            "Using stolen or unauthorized payment methods;",
            "Attempting to manipulate the Platform's payment system;",
            "Accepting bookings without the intent or ability to perform the requested services;",
            "Knowingly providing false information during account verification or investigations; or",
            "Participating in any fraudulent, deceptive, or illegal activity.",
          ],
        },

        { h: "24.3 Review Manipulation" },
        { p: "Vendors shall not directly or indirectly:" },
        {
          list: [
            "Purchase, sell, or exchange reviews;",
            "Submit reviews regarding their own business;",
            "Request friends, family members, or employees to submit misleading reviews;",
            "Offer discounts, gifts, refunds, incentives, or compensation in exchange for positive reviews;",
            "Submit false complaints regarding competing Vendors;",
            "Attempt to manipulate ratings or review rankings.",
          ],
        },
        { p: "The Company may remove any review it reasonably believes violates this Agreement or applicable law." },

        { h: "24.4 Misrepresentation" },
        { p: "Vendors shall not knowingly misrepresent:" },
        {
          list: [
            "Pricing;",
            "Availability;",
            "Experience;",
            "Certifications;",
            "Licenses;",
            "Insurance coverage;",
            "Menus;",
            "Services offered;",
            "Equipment;",
            "Staffing;",
            "Photographs;",
            "Customer testimonials; or",
            "Any material information likely to influence a Customer's booking decision.",
          ],
        },
        { p: "Any material changes to a Vendor's qualifications or business operations must be promptly updated on the Platform." },

        { h: "24.5 Bait-and-Switch Practices" },
        { p: "Vendors shall not advertise products or services at one price and then materially increase the price after the Customer has accepted the proposal without the Customer's prior written agreement." },
        { p: "Any additional charges must be clearly disclosed before they are incurred unless they result from material changes requested by the Customer." },

        { h: "24.6 Customer Protection" },
        { p: "The Company reserves the right to investigate complaints involving:" },
        {
          list: [
            "Failure to appear for a scheduled event;",
            "Habitual late arrivals;",
            "Poor service quality;",
            "Unsafe food handling;",
            "Unlicensed operations;",
            "Health and safety violations;",
            "Property damage;",
            "Threatening or abusive conduct;",
            "Discrimination prohibited by applicable law;",
            "Harassment;",
            "Fraudulent billing;",
            "Unauthorized charges; or",
            "Any conduct that may place Customers at unreasonable risk.",
          ],
        },

        { h: "24.7 Investigations" },
        { p: "The Company may investigate suspected violations of this Agreement." },
        { p: "During an investigation, the Company may request documents or information including:" },
        {
          list: [
            "Business licenses;",
            "Insurance certificates;",
            "Tax identification information;",
            "Event contracts;",
            "Invoices;",
            "Receipts;",
            "Communications conducted through the Platform;",
            "Photographs;",
            "Health permits;",
            "Other documentation reasonably necessary to investigate the matter.",
          ],
        },
        { p: "The Vendor agrees to cooperate fully and promptly with any reasonable investigation." },

        { h: "24.8 Temporary Suspension" },
        { p: "The Company may temporarily suspend a Vendor account while investigating suspected violations of this Agreement." },
        { p: "During any suspension, the Company may:" },
        {
          list: [
            "Pause new bookings;",
            "Delay payouts to the extent reasonably necessary;",
            "Remove listings from public view;",
            "Cancel pending bookings when reasonably necessary to protect Customers;",
            "Restrict access to Platform features.",
          ],
        },
        { p: "Suspension does not constitute a final determination regarding any alleged violation." },

        { h: "24.9 Permanent Removal" },
        { p: "The Company may permanently terminate a Vendor account for reasons including, but not limited to:" },
        {
          list: [
            "Repeated customer complaints supported by credible evidence;",
            "Fraud or attempted fraud;",
            "Intentional misrepresentation;",
            "Failure to maintain required licenses or insurance;",
            "Criminal activity affecting Platform safety or integrity;",
            "Repeated failure to perform booked services;",
            "Circumvention of Platform commissions;",
            "Serious violations of this Agreement; or",
            "Any conduct that the Company reasonably determines poses a significant risk to Customers, Vendors, or the Platform.",
          ],
        },
        { p: "Termination may occur with or without prior notice where permitted by applicable law and where immediate action is reasonably necessary to protect the Platform or its users." },

        { h: "24.10 Withholding of Funds" },
        { p: "To the extent permitted by applicable law and the terms of the applicable payment processor, the Company may temporarily withhold payouts that are reasonably associated with:" },
        {
          list: [
            "Pending chargebacks;",
            "Fraud investigations;",
            "Customer refund requests;",
            "Court orders;",
            "Government investigations;",
            "Compliance reviews; or",
            "Material violations of this Agreement.",
          ],
        },
        { p: "The Company shall not withhold funds longer than reasonably necessary to resolve the applicable issue." },

        { h: "24.11 Reporting to Authorities" },
        { p: "Nothing in this Agreement limits the Company's right to report suspected criminal activity, fraud, identity theft, financial crimes, health and safety violations, or other unlawful conduct to law enforcement agencies, regulatory authorities, payment processors, financial institutions, insurance carriers, or other appropriate entities." },

        { h: "24.12 Reservation of Rights" },
        { p: "The Company reserves all rights and remedies available under this Agreement, applicable law, and principles of equity." },
        { p: "The failure to enforce any provision of this Agreement shall not constitute a waiver of the Company's right to enforce that provision or any other provision in the future." },
      ],
    },
    {
      n: 25,
      title: "Vendor Performance Standards",
      body: [
        { h: "25.1 Professional Standards" },
        { p: "Vendors shall conduct themselves professionally, honestly, and courteously in all interactions with Customers and other Platform users." },
        { p: "Vendors shall perform all contracted services in a competent manner consistent with generally accepted industry standards and applicable laws." },

        { h: "25.2 Responsiveness" },
        { p: "Vendors agree to respond to Customer inquiries, booking requests, and Platform communications within a reasonable period of time." },
        { p: "Repeated failures to respond to booking requests or customer communications may negatively affect a Vendor's standing on the Platform and may result in account restrictions." },

        { h: "25.3 Accurate Listings" },
        { p: "Vendor profiles shall remain accurate and current at all times." },
        { p: "Vendors agree to promptly update:" },
        {
          list: [
            "Pricing;",
            "Menus;",
            "Service descriptions;",
            "Service areas;",
            "Business hours;",
            "Availability;",
            "Contact information;",
            "Licenses;",
            "Insurance information; and",
            "Any material changes affecting the Vendor's business.",
          ],
        },

        { h: "25.4 Pricing Transparency" },
        { p: "All pricing presented to Customers shall be truthful, accurate, and complete." },
        { p: "Vendors shall clearly disclose any additional fees before a Customer accepts a Proposal, including but not limited to:" },
        {
          list: [
            "Travel fees;",
            "Delivery charges;",
            "Setup or breakdown fees;",
            "Staffing charges;",
            "Equipment rental fees;",
            "Overtime charges;",
            "Taxes;",
            "Service charges; and",
            "Any other anticipated costs.",
          ],
        },
        { p: "Hidden fees or undisclosed charges are prohibited." },

        { h: "25.5 Calendar Accuracy" },
        { p: "Vendors are responsible for maintaining reasonably accurate availability on the Platform." },
        { p: "If a Vendor becomes unavailable for a confirmed Booking, the Vendor shall notify both the Customer and the Company as soon as reasonably practicable." },
        { p: "Repeated cancellations caused by inaccurate availability may result in account restrictions or termination." },

        { h: "25.6 Performance of Services" },
        { p: "Vendors agree to perform services substantially as described in their accepted Proposal." },
        { p: "Material changes to the agreed services may be made only with the Customer's consent." },

        { h: "25.7 Professional Conduct" },
        { p: "While using the Platform, Vendors shall not:" },
        {
          list: [
            "Use abusive, threatening, or harassing language;",
            "Discriminate in violation of applicable law;",
            "Engage in fraudulent or deceptive business practices;",
            "Solicit or accept bribes or kickbacks;",
            "Misrepresent products or services;",
            "Engage in illegal conduct; or",
            "Otherwise damage the reputation or integrity of the Platform.",
          ],
        },

        { h: "25.8 Health, Safety, and Legal Compliance" },
        { p: "Each Vendor shall comply with all applicable federal, state, and local laws, regulations, codes, permits, licensing requirements, and health and safety standards applicable to its business." },
        { p: "Where applicable, Vendors shall maintain all required food service permits, alcohol licenses, occupancy permits, transportation licenses, and other legally required authorizations." },

        { h: "25.9 Customer Satisfaction" },
        { p: "The Company values high-quality service and may consider Customer feedback, ratings, verified complaints, dispute history, booking completion rates, responsiveness, and other performance indicators when evaluating Vendor accounts." },
        { p: "The Company reserves the right to issue warnings, require corrective action, suspend, or terminate Vendor accounts that repeatedly fail to meet reasonable Platform standards." },
        { p: "Nothing in this Agreement guarantees that any Vendor will maintain a particular rating, receive a specific number of bookings, or remain listed on the Platform." },

        { h: "25.10 Cooperation with the Platform" },
        { p: "Vendors agree to cooperate with reasonable requests from the Company relating to:" },
        {
          list: [
            "Customer disputes;",
            "Booking verification;",
            "Fraud investigations;",
            "Chargebacks;",
            "Compliance reviews;",
            "Licensing verification;",
            "Insurance verification; and",
            "Platform safety investigations.",
          ],
        },
        { p: "Failure to cooperate may result in suspension or termination of the Vendor's account." },

        { h: "25.11 Platform Reputation" },
        { p: "Vendors acknowledge that their conduct reflects upon the Platform." },
        { p: "Accordingly, the Company may take appropriate action when a Vendor's conduct, whether on or off the Platform, materially harms the reputation, safety, trustworthiness, or integrity of the Platform or its users." },
        { p: "Nothing in this section is intended to restrict lawful activities protected by applicable law." },

        { h: "25.12 Continuous Improvement" },
        { p: "The Company may periodically establish or revise quality standards, operational requirements, verification procedures, or best practices designed to improve the safety, reliability, and overall experience of the Platform." },
        { p: "The Company will provide reasonable notice of material changes where required by applicable law, and Vendors agree to comply with such standards as a condition of continued participation on the Platform." },
      ],
    },
    {
      n: 26,
      title: "Booking Agreement; Accepted Terms Govern",
      body: [
        { p: "When a Customer accepts a Vendor Proposal on the Platform, the accepted Proposal together with the booking-specific terms displayed and accepted at that time forms the agreement between the Vendor and Customer for that Booking (the \"Booking Agreement\")." },
        { p: "The Booking Agreement includes the applicable elements presented at acceptance, which may include the Vendor and Customer identity; event date, time, and where applicable AM/PM period; event location if collected by the Platform; scope of services, packages, menus, quantities, or other selected items; pricing, deposit, remaining balance, and payment schedule; the cancellation and refund policy presented at acceptance; fees, taxes, and additional charges included in the Proposal; and any other terms expressly included in the accepted Proposal." },
        { p: "The version of the Vendor's cancellation policy, refund policy, and payment terms presented and accepted in connection with a particular Booking remains the policy governing that Booking, even if the Vendor later changes its general cancellation policy, refund policy, deposit requirements, or other standard terms." },
        { p: "Later changes to the Vendor's profile, pricing, standard policies, cancellation policy, refund policy, deposit requirements, or other general terms shall not retroactively alter an existing Booking Agreement." },
      ],
    },
    {
      n: 27,
      title: "Booking Modifications",
      body: [
        { p: "Material changes to an accepted Booking — including material changes to scope, price, services, event date, quantities, fees, or other material terms — require the agreement of both Customer and Vendor." },
        { p: "Where the Platform records such a modification, the modification becomes part of the Booking record. A recorded modification does not erase the historical accepted Booking terms." },
      ],
    },
    {
      n: 28,
      title: "Verified Vendor Designation",
      body: [
        { p: "A \"Verified\" designation or badge indicates only that the Company reviewed certain information or documentation submitted by the Vendor under the Company's then-current verification procedures. It is not an endorsement, warranty, or guarantee of quality, performance, safety, financial condition, indefinite validity of any credential, or suitability for any particular Customer or Event." },
        { p: "The Vendor is solely responsible for maintaining, in current and valid form, all licenses, permits, insurance, certifications, and other credentials legally required for the Vendor's business and services." },
        { p: "The Vendor shall keep verification information accurate and shall promptly update the Company or, where applicable, upload replacement documentation upon expiration, cancellation, suspension, revocation, or other material change to a submitted credential." },
        { p: "Submitting false, altered, or misleading verification information constitutes fraud under this Agreement and is subject to the remedies described elsewhere in this Agreement, including but not limited to suspension, removal, and reversal of payouts." },
      ],
    },
    {
      n: 29,
      title: "Disputes, Chargebacks, and Platform Records",
      body: [
        { p: "The Company may preserve, review, compile, and where reasonably necessary provide relevant Platform records when responding to refunds, payment disputes, chargebacks, cancellations, Vendor no-shows, Customer no-shows, fraud investigations, safety complaints, or compliance investigations." },
        { p: "Relevant records may include the accepted Proposal or Booking record, the applicable cancellation and refund policy, timestamps, payment and refund records, Platform communications, cancellation and no-show records, and uploaded supporting documentation." },
        { p: "The Company may provide relevant information to payment processors, financial institutions, regulators, law enforcement, or other appropriate entities where legally permitted and reasonably necessary, subject to the Company's Privacy Policy and applicable law." },
        { p: "The Company is not a court, arbitrator, insurer, guarantor, or ultimate fact-finder in Customer/Vendor service disputes." },
      ],
    },
    {
      n: 30,
      title: "Vendor Cancellations and Non-Performance",
      body: [
        { p: "Vendor-initiated cancellations, failure to appear, or failure to perform a booked Event may result, as appropriate and subject to the accepted Booking terms and applicable law, in Customer refund review, refund issuance, payout delay or hold, account investigation, warnings, temporary suspension, or removal from the Platform for repeated or serious violations." },
        { p: "This Article supplements — and does not replace — the Platform Integrity, suspension, and remedies provisions elsewhere in this Agreement." },
      ],
    },
    {
      n: 31,
      title: "Third-Party Services and Integrations",
      body: [
        { p: "The Platform integrates with independent third-party services (including payment processors, email/notification providers, hosting providers, and business-tool integrations Vendor may elect to connect)." },
        { p: "Where a Vendor elects to connect a supported third-party integration, the Vendor authorizes the Company to exchange information reasonably necessary to provide that integration, in accordance with the Privacy Policy and data-minimization principles." },
        { p: "The Company does not guarantee uninterrupted operation of independent third-party systems and, to the extent permitted by applicable law, is not responsible for outages, errors, or actions of such third parties." },
      ],
    },
    {
      n: 32,
      title: "Electronic Acceptance",
      body: [
        { p: "Where the Platform properly presents applicable terms, electronic actions such as creating an account, checking an acceptance checkbox, clicking an acceptance or confirmation control, sending a Proposal, accepting a Booking modification, or receiving a payment may constitute the Vendor's electronic acceptance of those terms." },
        { p: "Where the Platform records evidence of acceptance — including timestamps, user identifiers, and applicable document versions — that record may be used as evidence of the acceptance." },
      ],
    },
    {
      n: 33,
      title: "Changes to This Agreement",
      body: [
        { p: "The Company may modify this Agreement from time to time." },
        { p: "Material changes will be communicated through the Platform or by other reasonable means, and where required by applicable law, in advance of taking effect." },
        { p: "Changes to this general Agreement shall not retroactively modify the Booking-specific Proposal, pricing, cancellation policy, refund policy, or other terms already accepted for an existing Booking unless both Customer and Vendor agree in writing (including through recorded modification on the Platform) or applicable law requires otherwise." },
        { p: "Continued use of the Platform after changes become effective constitutes the Vendor's acceptance of the revised Agreement with respect to future activity." },
      ],
    },
    {
      n: 34,
      title: "Entire Agreement",
      body: [
        { p: "This Agreement, together with the Company's Privacy Policy and Payment, Cancellation & Refund Policy (each of which is incorporated by reference), and the accepted Booking Agreement for each individual Booking, constitutes the entire agreement between the Company and the Vendor regarding the subject matter herein and supersedes all prior understandings relating to that subject matter." },
        { p: "For the avoidance of doubt, incorporation of the Privacy Policy and Payment, Cancellation & Refund Policy is not intended to be excluded by this Article." },
      ],
    },
    {
      n: 35,
      title: "Contact Information",
      body: [
        { p: "Questions regarding this Agreement may be directed to:" },
        {
          contact: {
            company: "NContro",
            address: "2000 N Beauregard St #345, Alexandria, VA 22311",
            email: "scottye@ncontro.com",
            phone: "(901) 573-7661",
            website: "https://www.ncontro.com",
          },
        },
      ],
    },
  ],
  closing: "By creating a Vendor account, accepting bookings, or receiving payments through the Platform, the Vendor acknowledges that they have read, understood, and agree to be bound by this Agreement.",
};
